import { useEffect, useMemo, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { PHONE, PHONE_HREF, BOOKING_URL } from "@/data/site";

const BUTTON_STYLE = { layout: "vertical", shape: "rect", label: "pay", height: 40 };

const BookPayFallback = ({ itemId }) => (
  <div data-testid={`paypal-unavailable-${itemId}`}>
    <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-testid={`paypal-book-pay-btn-${itemId}`}
      className="block text-center bg-[#FFC439] hover:bg-[#F2BA36] text-[#003087] font-bold py-3.5 rounded-sm transition-colors duration-200">
      Book &amp; Pay with PayPal
    </a>
    <p className="text-xs text-zinc-500 mt-2">
      Secure checkout on our PayPal booking page. Prefer to talk? Call <a href={PHONE_HREF} className="text-brand font-medium">{PHONE}</a>.
    </p>
  </div>
);

const PaymentSuccess = ({ itemId }) => (
  <div data-testid={`paypal-success-${itemId}`} className="bg-white border border-brand rounded-sm p-4 text-center">
    <CheckCircle2 className="w-8 h-8 text-brand mx-auto mb-2" />
    <p className="font-heading font-bold text-brand-ink">Payment received</p>
    <p className="text-sm text-zinc-600 mt-1">Thanks! We'll call you to schedule. Need us sooner? Call {PHONE}.</p>
  </div>
);

const DUCT_CLEANING_PER_VENT_USD = 67;

const VentQtyPicker = ({ qty, setQty }) => (
  <label className="flex items-center gap-2 text-sm text-zinc-700 mb-3">
    Number of vents:
    <input data-testid="paypal-vent-qty" type="number" min="1" max="50" value={qty}
      onChange={(e) => setQty(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
      className="w-20 border border-zinc-300 rounded-sm px-2 py-1.5 text-sm" />
    <span className="font-heading font-bold text-brand">${(DUCT_CLEANING_PER_VENT_USD * qty).toFixed(2)}</span>
  </label>
);

export const PayPalCheckout = ({ itemId }) => {
  const [cfg, setCfg] = useState(null);
  const [qty, setQty] = useState(8);
  const [status, setStatus] = useState("idle"); // idle | paid | error

  useEffect(() => {
    api.get("/paypal/config").then((r) => setCfg(r.data)).catch(() => setCfg(false));
  }, []);

  const scriptOptions = useMemo(
    () => (cfg ? { clientId: cfg.client_id, currency: "USD", intent: "capture" } : null),
    [cfg]
  );
  const forceReRender = useMemo(() => [qty, itemId], [qty, itemId]);

  if (cfg === null) return null;
  if (cfg === false || !cfg.enabled) return <BookPayFallback itemId={itemId} />;
  if (status === "paid") return <PaymentSuccess itemId={itemId} />;

  return (
    <div data-testid={`paypal-checkout-${itemId}`}>
      {itemId === "duct-cleaning" && <VentQtyPicker qty={qty} setQty={setQty} />}
      <PayPalScriptProvider options={scriptOptions}>
        <PayPalButtons
          style={BUTTON_STYLE}
          forceReRender={forceReRender}
          createOrder={async () => {
            const r = await api.post("/paypal/orders", { item_id: itemId, quantity: qty });
            return r.data.id;
          }}
          onApprove={async (data) => {
            try {
              await api.post(`/paypal/orders/${data.orderID}/capture`);
              setStatus("paid");
            } catch (e) {
              setStatus("error");
            }
          }}
          onError={() => setStatus("error")}
        />
      </PayPalScriptProvider>
      {status === "error" && (
        <p data-testid={`paypal-error-${itemId}`} className="text-sm text-brand-red mt-2">
          Payment could not be completed. Please try again or call {PHONE}.
        </p>
      )}
    </div>
  );
};
